import React, { useMemo } from 'react';
import { bloomberg } from '../theme/bloombergTheme';

interface MacroSparklineProps {
  /** Evenly-sampled values, oldest first. */
  values: number[];
  width?: number;
  height?: number;
  /** Defaults to muted ink. Deliberately NOT status-coloured — the delta
   *  column carries good/bad, so tinting the line too would double-encode it
   *  and make every panel read as a wall of red and green. */
  color?: string;
}

/**
 * Inline SVG sparkline — shape only, no axis, no labels, no tooltip.
 *
 * Hand-rolled rather than a recharts <LineChart>: a panel renders up to 59 of
 * these at once, and recharts mounts a ResponsiveContainer and a resize
 * observer per instance. This is one <path>.
 */
const MacroSparkline: React.FC<MacroSparklineProps> = ({
  values,
  width = 84,
  height = 22,
  color,
}) => {
  const stroke = color || bloomberg.text.muted;

  const path = useMemo(() => {
    if (values.length < 2) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min;
    // A flat series would divide by zero — draw it down the middle instead.
    const y = (v: number) =>
      span === 0 ? height / 2 : height - 1 - ((v - min) / span) * (height - 2);
    const x = (i: number) => (i / (values.length - 1)) * width;
    return values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  }, [values, width, height]);

  if (!path) {
    return <svg width={width} height={height} aria-hidden />;
  }

  return (
    <svg width={width} height={height} role="img" aria-label="12-month trend">
      <path
        d={path}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

export default MacroSparkline;
